import { useState, useEffect } from 'react';
import { sendBroadcast } from '../lib/broadcast';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import '../styles/BroadcastModal.css';

const BroadcastModal = ({ isOpen, onClose, groupId, eventId, groupName, eventTitle, user }) => {
  const [userData, setUserData] = useState(null);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const MAX_LENGTH = 160; // Standard SMS length
  const remainingChars = MAX_LENGTH - message.length;
  const isGroup = !!groupId;

  // Load user data to get firstName/lastName
  useEffect(() => {
    if (isOpen && user) {
      const loadUserData = async () => {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            setUserData(userDoc.data());
          }
        } catch (error) {
          console.error('Error loading user data:', error);
        }
      };
      loadUserData();
    }
  }, [isOpen, user]);

  const handleClose = () => {
    setMessage('');
    setError('');
    setSuccess(false);
    onClose();
  };

  const handleSend = async () => {
    if (!message.trim()) {
      setError('Please enter a message');
      return;
    }

    if (message.length > MAX_LENGTH) {
      setError(`Message must be ${MAX_LENGTH} characters or less`);
      return;
    }

    setSending(true);
    setError('');
    setSuccess(false);

    try {
      const senderName = userData 
        ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || 'Host'
        : 'Host';

      await sendBroadcast({
        groupId,
        eventId,
        message: message.trim(),
        senderId: user.uid,
        senderName,
      });

      setSuccess(true);
      setMessage('');
      
      // Close after 1.5 seconds
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (err) {
      console.error('Error sending broadcast:', err);
      setError(err.message || 'Failed to send broadcast. Please try again.');
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="broadcast-modal-overlay" onClick={handleClose}>
      <div className="broadcast-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="broadcast-modal-header">
          <h2>📢 Broadcast Message</h2>
          <button className="broadcast-modal-close" onClick={handleClose}>×</button>
        </div>
        
        <div className="broadcast-modal-body">
          <div className="broadcast-info">
            <p>
              Send a message to all {isGroup ? 'members' : 'attendees'} of{' '}
              <strong>{isGroup ? groupName : eventTitle}</strong>
            </p>
            <p className="broadcast-info-note">
              This will send a notification and SMS to all {isGroup ? 'members' : 'attendees'}.
            </p>
          </div>

          <div className="broadcast-input-group">
            <textarea
              className="broadcast-input"
              placeholder={`Enter your message (max ${MAX_LENGTH} characters)...`}
              value={message}
              onChange={(e) => {
                const newValue = e.target.value;
                if (newValue.length <= MAX_LENGTH) {
                  setMessage(newValue);
                  setError('');
                }
              }}
              rows={4}
              maxLength={MAX_LENGTH}
              disabled={sending}
            />
            <div className="broadcast-char-count" style={{ 
              color: remainingChars < 20 ? 'var(--error)' : 'var(--text-secondary)' 
            }}>
              {remainingChars} characters remaining
            </div>
          </div>

          {error && (
            <div className="broadcast-error">
              {error}
            </div>
          )}

          {success && (
            <div className="broadcast-success">
              ✅ Broadcast sent successfully!
            </div>
          )}

          <div className="broadcast-actions">
            <button
              className="btn btn-secondary"
              onClick={handleClose}
              disabled={sending}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSend}
              disabled={sending || !message.trim() || message.length > MAX_LENGTH}
            >
              {sending ? 'Sending...' : 'Send Broadcast'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BroadcastModal;
